import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  LinkIcon, Type, Mic, Plus, Loader2, Upload, FileUp, X,
  StickyNote, Check, Sparkles,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { VoiceRecorder, VOICE_SUPPORTED } from '../components/VoiceRecorder';
import { CategoryPicker } from '../components/CategoryPicker';
import BasicSaveConfirmModal from '../components/BasicSaveConfirmModal';
import InspireMeDrawer from '../components/InspireMeDrawer';

const API = '/api';

/**
 * AddBookmarkModal - extracted from Dashboard.js to reduce component size.
 * Manages the URL / Manual / Voice tabs and handles form submission.
 */
export default function AddBookmarkModal({
  open,
  onOpenChange,
  categories,
  getCategoryIcon,
  onBookmarkAdded,
  onSessionExpired,
  onLimitReached,
  onUpgradeRequest,
  onCategoryCreated,
  initialUrl = '',
  initialMode = 'url',
  initialFile = null,
}) {
  const [addMode, setAddMode] = useState(initialMode);
  const [newUrl, setNewUrl] = useState(initialUrl);
  const [newTags, setNewTags] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newCategoryIds, setNewCategoryIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [manualText, setManualText] = useState('');
  const [manualFile, setManualFile] = useState(initialFile);
  const [voiceText, setVoiceText] = useState('');
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [loadingSuggestedTags, setLoadingSuggestedTags] = useState(false);
  const [suggestedCatIds, setSuggestedCatIds] = useState([]);
  const [loadingSuggestedCats, setLoadingSuggestedCats] = useState(false);
  const fileInputRef = useRef(null);
  const voiceTagTimerRef = useRef(null);
  const catSuggestTimerRef = useRef(null);

  // Premium AI quota gating - if the user is over their AI quota we prompt
  // them to either save as Basic (no AI) or upgrade. Pre-flighted here
  // instead of after a server 4xx so the user keeps their typed input.
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [basicConfirmOpen, setBasicConfirmOpen] = useState(false);
  const pendingSubmitRef = useRef(null);

  // Refresh quota state whenever the modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/subscriptions/limit-check`, { credentials: 'include' });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setQuotaInfo(data);
        }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  /** Returns true if we already know the user is over the AI quota. */
  const isOverQuota = () => quotaInfo && quotaInfo.is_premium_eligible === false;

  /** Wraps a real submit so we show the Basic-save modal first when over quota. */
  const gatedSubmit = (submitFn) => {
    if (isOverQuota()) {
      pendingSubmitRef.current = submitFn;
      setBasicConfirmOpen(true);
      return;
    }
    submitFn();
  };

  const handleConfirmBasicSave = () => {
    setBasicConfirmOpen(false);
    const fn = pendingSubmitRef.current;
    pendingSubmitRef.current = null;
    if (fn) fn();
  };

  const handleConfirmUpgrade = () => {
    setBasicConfirmOpen(false);
    pendingSubmitRef.current = null;
    onOpenChange(false);
    onUpgradeRequest?.(quotaInfo);
  };

  // Sync initialUrl/initialMode/initialFile when they change externally
  useEffect(() => { setNewUrl(initialUrl); }, [initialUrl]);
  useEffect(() => { setAddMode(initialMode); }, [initialMode]);
  useEffect(() => { if (initialFile) setManualFile(initialFile); }, [initialFile]);

  const resetForm = useCallback(() => {
    setAddMode('url');
    setManualText('');
    setManualFile(null);
    setVoiceText('');
    setSuggestedTags([]);
    setSuggestedCatIds([]);
    setLoadingSuggestedCats(false);
    setNewCategoryIds([]);
    setNewTags('');
    setNewNotes('');
    setNewUrl('');
  }, []);

  const toggleCategory = useCallback((id) => {
    setNewCategoryIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  // Auto-suggest categories when URL changes (debounced)
  useEffect(() => {
    if (catSuggestTimerRef.current) clearTimeout(catSuggestTimerRef.current);
    if (!newUrl.trim() || newUrl.trim().length < 5 || addMode !== 'url') {
      setSuggestedCatIds([]);
      setLoadingSuggestedCats(false);
      return;
    }
    setLoadingSuggestedCats(true);
    catSuggestTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/bookmarks/suggest-categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ url: newUrl.trim() }),
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestedCatIds(data.suggested_ids || []);
        }
      } catch (_) { /* silent */ }
      setLoadingSuggestedCats(false);
    }, 600);
    return () => { if (catSuggestTimerRef.current) clearTimeout(catSuggestTimerRef.current); };
  }, [newUrl, addMode]);

  // Auto-suggest tags for voice (debounced)
  useEffect(() => {
    if (voiceTagTimerRef.current) clearTimeout(voiceTagTimerRef.current);
    if (!voiceText.trim() || voiceText.trim().length < 15 || addMode !== 'voice') {
      setSuggestedTags([]);
      return;
    }
    setLoadingSuggestedTags(true);
    voiceTagTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/bookmarks/suggest-tags`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text: voiceText.trim() }),
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestedTags(data.tags || []);
        }
      } catch (_) { /* silent */ }
      setLoadingSuggestedTags(false);
    }, 1200);
    return () => { if (voiceTagTimerRef.current) clearTimeout(voiceTagTimerRef.current); };
  }, [voiceText, addMode]);

  // Auto-analyze uploaded image - pre-suggest categories + detect product (Amazon CTA)
  const lastAnalyzedFileRef = useRef(null);
  const [productInfo, setProductInfo] = useState(null);
  useEffect(() => {
    if (!manualFile || addMode !== 'manual') {
      setProductInfo(null);
      return;
    }
    if (!manualFile.type?.startsWith('image/')) return;
    // Avoid re-analyzing the exact same file twice
    const key = `${manualFile.name}::${manualFile.size}::${manualFile.lastModified || ''}`;
    if (lastAnalyzedFileRef.current === key) return;
    lastAnalyzedFileRef.current = key;

    let cancelled = false;
    (async () => {
      setLoadingSuggestedCats(true);
      try {
        const fd = new FormData();
        fd.append('file', manualFile);
        const res = await fetch(`${API}/bookmarks/analyze-image`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;

        if (Array.isArray(data.suggested_category_ids) && data.suggested_category_ids.length) {
          setSuggestedCatIds(data.suggested_category_ids);
        }
        if (Array.isArray(data.tags) && data.tags.length) {
          setSuggestedTags(data.tags);
        }
        if (data.is_product && data.product_name) {
          setProductInfo({ product_name: data.product_name, brand: data.brand || null });
        } else {
          setProductInfo(null);
        }
      } catch (_) { /* silent */ }
      if (!cancelled) setLoadingSuggestedCats(false);
    })();
    return () => { cancelled = true; };
  }, [manualFile, addMode]);

  // ── URL submit ──
  const handleAddBookmark = async (e) => {
    e.preventDefault();
    if (!newUrl.trim()) return;
    gatedSubmit(() => doAddBookmark());
  };

  const doAddBookmark = async () => {
    let url = newUrl.trim();
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/bookmarks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          url,
          tags: newTags.split(',').map(t => t.trim()).filter(Boolean),
          category_ids: newCategoryIds.length ? newCategoryIds : [],
          notes: newNotes.trim() || null,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        onOpenChange(false);
        resetForm();
        onBookmarkAdded(data);
      } else {
        if (response.status === 401) {
          onOpenChange(false);
          onSessionExpired?.();
          return;
        }
        const err = await response.json().catch(() => ({}));
        const detail = typeof err.detail === 'object' ? err.detail : { message: err.detail };
        if (response.status === 403 && detail.error === 'bookmark_limit_reached') {
          onOpenChange(false);
          onLimitReached?.(detail);
          return;
        }
        throw new Error(detail.message || 'Failed to add bookmark');
      }
    } catch (error) {
      const { toast } = await import('sonner');
      toast.error(error.message || 'Error adding bookmark');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Manual submit ──
  const handleAddManualBookmark = async (e) => {
    e.preventDefault();
    if (!manualText.trim() && !manualFile) return;
    gatedSubmit(() => doAddManualBookmark());
  };

  const doAddManualBookmark = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      if (manualText.trim()) formData.append('text_content', manualText.trim());
      if (manualFile) formData.append('file', manualFile);
      formData.append('tags', newTags);
      formData.append('category_ids', newCategoryIds.join(','));
      if (newNotes.trim()) formData.append('notes', newNotes.trim());

      const response = await fetch(`${API}/bookmarks/manual`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (response.ok) {
        const manualData = await response.json();
        onOpenChange(false);
        resetForm();
        setAddMode('url');
        onBookmarkAdded(manualData);
      } else {
        if (response.status === 401) {
          onOpenChange(false);
          onSessionExpired?.();
          return;
        }
        const err = await response.json().catch(() => ({}));
        const detail = typeof err.detail === 'object' ? err.detail : { message: err.detail };
        if (response.status === 403 && detail.error === 'bookmark_limit_reached') {
          onOpenChange(false);
          onLimitReached?.(detail);
          return;
        }
        throw new Error(detail.message || 'Failed to add note');
      }
    } catch (error) {
      const { toast } = await import('sonner');
      toast.error(error.message || 'Error adding manual bookmark');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Voice submit ──
  const handleAddVoiceBookmark = async () => {
    if (!voiceText.trim()) return;
    gatedSubmit(() => doAddVoiceBookmark());
  };

  const doAddVoiceBookmark = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('text_content', voiceText.trim());
      formData.append('tags', newTags);
      formData.append('category_ids', newCategoryIds.join(','));
      if (newNotes.trim()) formData.append('notes', newNotes.trim());

      const response = await fetch(`${API}/bookmarks/manual`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (response.ok) {
        const voiceData = await response.json();
        onOpenChange(false);
        resetForm();
        setAddMode('url');
        onBookmarkAdded(voiceData);
      } else {
        if (response.status === 401) {
          onOpenChange(false);
          onSessionExpired?.();
          return;
        }
        const err = await response.json().catch(() => ({}));
        const detail = typeof err.detail === 'object' ? err.detail : { message: err.detail };
        if (response.status === 403 && detail.error === 'bookmark_limit_reached') {
          onOpenChange(false);
          onLimitReached?.(detail);
          return;
        }
        throw new Error(detail.message || 'Failed to save voice note');
      }
    } catch (error) {
      const { toast } = await import('sonner');
      toast.error(error.message || 'Error saving voice bookmark');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <BasicSaveConfirmModal
        open={basicConfirmOpen}
        onClose={() => { setBasicConfirmOpen(false); pendingSubmitRef.current = null; }}
        onSaveBasic={handleConfirmBasicSave}
        onUpgrade={handleConfirmUpgrade}
        premiumUsed={quotaInfo?.premium_used ?? 0}
        premiumLimit={quotaInfo?.premium_limit ?? 20}
        tier={quotaInfo?.tier ?? 'free'}
      />
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto overscroll-contain">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Add Bookmark</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Save a URL, text note, or voice memo.
          </DialogDescription>
        </DialogHeader>

        {/* Mode Toggle */}
        <div className="flex rounded-xl bg-secondary p-1 mt-2" data-testid="add-mode-toggle">
          <button type="button" onClick={() => setAddMode('url')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${addMode === 'url' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="add-mode-url">
            <LinkIcon className="w-4 h-4" /> URL
          </button>
          <button type="button" onClick={() => setAddMode('manual')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${addMode === 'manual' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="add-mode-manual">
            <Type className="w-4 h-4" /> Text / File
          </button>
          {VOICE_SUPPORTED && (
            <button type="button" onClick={() => setAddMode('voice')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${addMode === 'voice' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="add-mode-voice">
              <Mic className="w-4 h-4" /> Voice
            </button>
          )}
        </div>

        {/* ─── URL Form ─── */}
        {addMode === 'url' ? (
          <form onSubmit={handleAddBookmark} className="space-y-4 mt-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">URL *</label>
              <Input placeholder="e.g. youtube.com/watch?v=... or emergent.sh" value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)} className="rounded-xl" autoFocus data-testid="add-url-input" />
              <InspireMeDrawer
                onPick={(exampleUrl) => setNewUrl(exampleUrl)}
                hidden={!!newUrl.trim()}
              />
            </div>
            <CategoryPicker categories={categories} selectedIds={newCategoryIds}
              onToggle={toggleCategory} getCategoryIcon={getCategoryIcon} suggestedIds={suggestedCatIds}
              loading={loadingSuggestedCats} hasUrl={!!newUrl.trim()} testId="add-category-select"
              bookmarkContext={{ url: newUrl.trim() }}
              onCategoryCreated={onCategoryCreated}
            />
            {/* Tags field intentionally removed from the URL form - they
                are auto-generated server-side after AI processing and
                are rarely meaningful to edit at save time. Users can
                still edit them later from the bookmark's Edit view. */}
            <div>
              <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
                <StickyNote className="w-4 h-4" /> Notes
              </label>
              <Textarea placeholder="Why are you saving this?" value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)} className="rounded-xl min-h-[80px] resize-none" data-testid="add-notes-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl bg-primary" disabled={submitting || !newUrl.trim()} data-testid="submit-bookmark-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add
              </Button>
            </div>
          </form>

        ) : addMode === 'manual' ? (
          /* ─── Manual Form ─── */
          <form onSubmit={handleAddManualBookmark} className="space-y-4 mt-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Text Content *</label>
              <Textarea placeholder="Paste or type your text here..." value={manualText}
                onChange={(e) => setManualText(e.target.value)} className="rounded-xl min-h-[120px] resize-none" autoFocus data-testid="manual-text-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
                <Upload className="w-4 h-4" /> Or upload a file
              </label>
              <div className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${manualFile ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                onClick={() => fileInputRef.current?.click()} data-testid="file-upload-area">
                <input ref={fileInputRef} type="file" accept=".txt,.md,.csv,.json,.xml,.html,.rtf,.pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.heic,.heif,image/*" className="hidden"
                  onChange={(e) => { const file = e.target.files?.[0]; if (file) { setManualFile(file); setManualText(''); } }} data-testid="file-input" />
                {manualFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileUp className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium text-primary">{manualFile.name}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setManualFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="ml-1 p-0.5 rounded-full hover:bg-secondary">
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                ) : (
                  <div className="text-muted-foreground">
                    <FileUp className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-sm">Click to upload a file</p>
                    <p className="text-xs mt-0.5">Images, PDF, Word, or text files</p>
                  </div>
                )}
              </div>
            </div>
            <CategoryPicker categories={categories} selectedIds={newCategoryIds}
              onToggle={toggleCategory} getCategoryIcon={getCategoryIcon} testId="manual-category-select"
              bookmarkContext={{ title: manualText.slice(0, 120), summary: manualText.slice(0, 600) }}
              onCategoryCreated={onCategoryCreated}
            />
            {/* Tags field intentionally removed from the manual form -
                see comment in URL form above. Auto-generated tags
                still run server-side. */}
            <div>
              <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
                <StickyNote className="w-4 h-4" /> Notes
              </label>
              <Textarea placeholder="Why are you saving this?" value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)} className="rounded-xl min-h-[80px] resize-none" data-testid="manual-notes-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl bg-primary" disabled={submitting || (!manualText.trim() && !manualFile)} data-testid="submit-manual-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add Note
              </Button>
            </div>
          </form>

        ) : (
          /* ─── Voice Form ─── */
          <div className="space-y-4 mt-3" data-testid="voice-tab-content">
            <VoiceRecorder onTranscript={setVoiceText} disabled={submitting} />

            {voiceText.trim() && (
              <>
                <CategoryPicker categories={categories} selectedIds={newCategoryIds}
                  onToggle={toggleCategory} getCategoryIcon={getCategoryIcon} testId="voice-category-select"
                  bookmarkContext={{ title: voiceText.slice(0, 120), summary: voiceText.slice(0, 600) }}
                  onCategoryCreated={onCategoryCreated}
                />

                <div>
                  <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4" /> Tags
                    {loadingSuggestedTags && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                  </label>
                  {suggestedTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="voice-suggested-tags">
                      {suggestedTags.map(tag => {
                        const currentTags = newTags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
                        const isAdded = currentTags.includes(tag.toLowerCase());
                        return (
                          <button key={tag} type="button"
                            onClick={() => { if (!isAdded) setNewTags(prev => prev ? `${prev}, ${tag}` : tag); }}
                            disabled={isAdded}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all border ${
                              isAdded ? 'bg-primary/10 text-primary border-primary/30 opacity-60'
                                : 'bg-secondary/50 text-foreground border-border hover:bg-primary/10 hover:border-primary/30 hover:text-primary'
                            }`} data-testid={`suggested-tag-${tag}`}>
                            {isAdded ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {tag}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <Input placeholder="Optional: ai, tech, learning" value={newTags}
                    onChange={(e) => setNewTags(e.target.value)} className="rounded-xl" data-testid="voice-tags-input" />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
                    <StickyNote className="w-4 h-4" /> Notes
                  </label>
                  <Textarea placeholder="Add extra context..." value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)} className="rounded-xl min-h-[80px] resize-none" data-testid="voice-notes-input" />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
                  <Button type="button" className="flex-1 rounded-xl bg-primary" disabled={submitting || !voiceText.trim()}
                    onClick={handleAddVoiceBookmark} data-testid="submit-voice-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4 mr-1" />} Save Voice Note
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
